import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import _findIndex from 'lodash/findIndex';
import _includes from 'lodash/includes';

import './ButtonSelectorDropdown.less';

const ButtonSelectorDropdown = props => {
  const {
    label,
    ariaLabel,
    dropdownItems,
    onDropdownItemClick,
    menuPositionX,
    menuPositionY,
  } = props;

  const [showMenu, setShowMenu] = useState(false);
  const [selectedDropdownItem, setSelectedDropdownItem] = useState(undefined);
  const overflowMenuNode = useRef();

  const handleMenuOpen = () => {
    setShowMenu(true);
  };

  const handleMenuClose = () => {
    setShowMenu(false);
    setSelectedDropdownItem(undefined);
  };

  const handleMenuItemChange = key => {
    setSelectedDropdownItem(key);
  };

  const handleMenuClick = useCallback(
    value => {
      onDropdownItemClick(value);
      handleMenuClose();
    },
    [onDropdownItemClick]
  );

  useEffect(() => {
    const handleOutsideClick = e => {
      if (!overflowMenuNode.current.contains(e.target)) {
        handleMenuClose();
      }
    };

    const handleArrowDown = () => {
      const selectedIndex = selectedDropdownItem
        ? _findIndex(dropdownItems, ['id', selectedDropdownItem.id])
        : undefined;

      // Choose the next item down if one is selected and not last or choose the first item
      if (selectedIndex >= 0 && selectedIndex < dropdownItems.length - 1) {
        setSelectedDropdownItem(dropdownItems[selectedIndex + 1]);
      } else {
        setSelectedDropdownItem(dropdownItems[0]);
      }
    };

    const handleArrowUp = () => {
      const selectedIndex = selectedDropdownItem
        ? _findIndex(dropdownItems, ['id', selectedDropdownItem.id])
        : undefined;

      // Choose the next item up if one is selected and not first or choose the last item
      if (selectedIndex > 0) {
        setSelectedDropdownItem(dropdownItems[selectedIndex - 1]);
      } else {
        setSelectedDropdownItem(dropdownItems[dropdownItems.length - 1]);
      }
    };

    const handleKeydown = e => {
      if (selectedDropdownItem && _includes(['Enter', 'ArrowRight'], e.key)) {
        e.preventDefault();
        handleMenuClick(selectedDropdownItem.value);
      } else if (_includes(['Escape', 'Esc', 'Tab'], e.key)) {
        e.preventDefault();
        handleMenuClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        handleArrowDown();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        handleArrowUp();
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('keydown', handleKeydown);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeydown);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [showMenu, selectedDropdownItem, dropdownItems, handleMenuClick]);

  const toggleDropdown = () => {
    if (!showMenu) {
      handleMenuOpen();
    } else {
      handleMenuClose();
    }
  };

  return (
    <div
      className="button-selector-dropdown"
      ref={overflowMenuNode}
      onMouseLeave={handleMenuItemChange}
    >
      <button
        className={cx('button-selector-dropdown__button', {
          'button-selector-dropdown__button--active': showMenu,
        })}
        type="button"
        aria-label={ariaLabel}
        onClick={toggleDropdown}
        data-testid="button-dropdown"
      >
        {label}
      </button>
      {showMenu && (
        <ul
          className={cx(
            'button-selector-dropdown__dropdown-menu',
            `button-selector-dropdown__dropdown-menu-${menuPositionX}`,
            `button-selector-dropdown__dropdown-menu-${menuPositionY}`
          )}
          data-testid="dropdown"
        >
          {dropdownItems.map(dropdownItem => (
            <li
              key={dropdownItem.id}
              className={cx('button-selector-dropdown__dropdown-item', {
                'button-selector-dropdown__dropdown-item--active':
                  selectedDropdownItem === dropdownItem,
              })}
              data-testid="dropdown-option"
              onClick={() => handleMenuClick(dropdownItem.value)}
              onMouseEnter={() => handleMenuItemChange(dropdownItem)}
            >
              {dropdownItem.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

ButtonSelectorDropdown.propTypes = {
  label: PropTypes.node,
  ariaLabel: PropTypes.string,
  dropdownItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
    })
  ).isRequired,
  onDropdownItemClick: PropTypes.func.isRequired,
  menuPositionX: PropTypes.oneOf(['right', 'left']),
  menuPositionY: PropTypes.oneOf(['top', 'bottom']),
};

ButtonSelectorDropdown.defaultProps = {
  ariaLabel: 'More Options',
  menuPositionX: 'left',
  menuPositionY: 'bottom',
};

export default ButtonSelectorDropdown;



// WEBPACK FOOTER //
// ./src/components/common/zolaUI/ButtonSelectorDropdown/ButtonSelectorDropdown.jsx